/*
 * Copyright (c) 2017-2019 by Gary Kumfert. All rights reserved.
 *
 * MIT License
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy of this software
 * and associated documentation files (the "Software"), to deal in the Software without restriction,
 * including without limitation the rights to use, copy, modify, merge, publish, distribute,
 * sublicense, and/or sell copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all copies or
 * substantial portions of the Software.*/

import { Injectable } from '@angular/core';

import { DNDItemDirective } from './dnd.item.directive';
import { DNDContainerDirective } from './dnd.container.directive';

export enum DNDMode {
    UNKNOWN,
    COPY, // always from a src to a target container. The source doesn't delete.
    MOVE, // always from a src to a target container. The source does delete.
    SORT, // always within a container, and container must be sortable
    DROP,
    DELETE,
    CANCEL,
}

// accumulates state
export class DNDInfo {

    mode: DNDMode = DNDMode.UNKNOWN;

    originalItem: DNDItemDirective;
    dragData: any;
    originalContainer: DNDContainerDirective;
    originalIndex: number = -1;
    moveOrCopy: DNDMode; // move or copy only

    numberEnters: number = 0;
    numberLeaves: number = 0;

    targetItem: DNDItemDirective;
    targetContainer: DNDContainerDirective;
    index: number = -1;

}

@Injectable()
export class DNDService {

    private static _sharedInfo: DNDInfo = new DNDInfo();

    private get sharedInfo(): any {
        return DNDService._sharedInfo;
    }

    public get isDragging(): boolean {
        switch (this.sharedInfo.mode) {
            case DNDMode.COPY:
            case DNDMode.MOVE:
            case DNDMode.SORT:
                return true;

            case DNDMode.UNKNOWN:
            case DNDMode.DROP:
            case DNDMode.DELETE:
            case DNDMode.CANCEL:
            default:
                return false;
        }
    }

    public onNewDNDItem(src: DNDItemDirective): void {
        // this gets triggered when an item is copied into a new container
        this.sharedInfo.targetItem = src;
        this.sharedInfo.mode = this.sharedInfo.moveOrCopy;
        src.decorateForMode(this.sharedInfo.mode);
    }

    // draggable side
    onDragStart(event: any, src: DNDItemDirective): DNDInfo {

        // updates
        event.dataTransfer.setData('any', src.el.nativeElement);

        // save
        DNDService._sharedInfo = new DNDInfo();
        this.sharedInfo.originalItem = src;
        this.sharedInfo.dragData = src.dragData ? src.dragData : src.el.nativeElement;
        this.sharedInfo.index = src.index; // may be -1
        this.sharedInfo.originalIndex = src.index; // may be -1
        this.sharedInfo.moveOrCopy = src.container.copyNotMove ? DNDMode.COPY : DNDMode.MOVE;
        this.sharedInfo.mode = (this.sharedInfo.index) ? DNDMode.SORT : this.sharedInfo.moveOrCopy;
        this.sharedInfo.targetItem = src;
        this.sharedInfo.targetContainer = src.container;
        this.sharedInfo.originalContainer = src.container;

        return this.sharedInfo;
    }

    onDragContinue(event: any, src: DNDItemDirective): DNDInfo {
        return this.sharedInfo;
    }

    onDragEnd(event: any, src: DNDItemDirective): DNDInfo {
        if (this.sharedInfo.mode !== DNDMode.DROP) { // no drop event? it must be a cancel or delete

            if (this.sharedInfo.originalContainer.deleteOnDragOff && !this.sharedInfo.originalContainer.copyNotMove) {
                this.removeFromCurrentMidDrag(event);
                this.sharedInfo.mode = DNDMode.DELETE;
            } else {
                this.removeFromCurrentMidDrag(event);
                this.insertToTargetMidDrag(event, this.sharedInfo.originalContainer, this.sharedInfo.originalIndex);
                this.sharedInfo.mode = DNDMode.CANCEL;
            }
        }
        /*
        if (this.sharedInfo.targetItem) {
            this.sharedInfo.targetItem.decorateForMode(this.sharedInfo.mode);
            this.sharedInfo.targetItem = undefined;
        }
        */

        return this.sharedInfo;
    }

    // droppable side
    onDragEnter(event: any, dest: DNDContainerDirective): DNDInfo {
        this.sharedInfo.numberEnters += 1;

        // this can happen for two reasons:
        //   1. it is legitimately entering the container, in which case we add to the end
        //   2. it already exists in the container (due to onDragOverItem()) and in which case, we do nothing.

        return this.onDragOver(event, dest);
    }

    // alt == alternate item, the one being pushed out of the way for the draggable.
    onDragOverItem(event: any, alt: DNDItemDirective): DNDInfo {

        if (alt.container !== this.sharedInfo.targetContainer) {
            // item has shifted containers
            this.removeFromCurrentMidDrag(event);
            this.insertToTargetMidDrag(event, alt.container, alt.index);

        } else if (alt.index !== this.sharedInfo.index) {
            // item has shifted inside container
            this.moveInCurrentMidDrag(event, alt);
        }

        // cache data;
        this.sharedInfo.targetContainer = alt.container;
        this.sharedInfo.index = alt.index;

        return this.sharedInfo;
    }

    onDragOver(event: any, dest: DNDContainerDirective): DNDInfo {
        this.sharedInfo.mode = this.sharedInfo.originalContainer === dest ? DNDMode.SORT : this.sharedInfo.moveOrCopy;
        if (this.sharedInfo.targetContainer !== dest) {
            // this happens when dragging into new container and either not hitting
            // an item first, or there are no items
            this.removeFromCurrentMidDrag(event);
            if (dest.isSortable) {
                this.sharedInfo.index = dest.sortableData.length;
                this.insertToTargetMidDrag(event, dest, this.sharedInfo.index);
            }
            this.sharedInfo.targetContainer = dest;
        }
        return this.sharedInfo;
    }

    onDragLeave(event: any, dest: DNDContainerDirective): DNDInfo {
        this.sharedInfo.numberLeaves += 1;

        // TODO: BUG TO FIX
        //  Steps.  1. Drag from one container to a second, must land in the first position only!
        //          2. continue dragging out past any container.
        //  Expected result,  item is removed from 2nd container.
        //  Observed result,  item remains in first position in 2nd container.
        if (this.sharedInfo.numberEnters === this.sharedInfo.numberLeaves) {
            if (this.sharedInfo.originalContainer.deleteOnDragOff) {
                // its got no home!
                this.removeFromCurrentMidDrag(event);
                this.sharedInfo.targetContainer = undefined;
                this.sharedInfo.index = -1;
            } else {
                this.removeFromCurrentMidDrag(event);
                this.insertToTargetMidDrag(event, this.sharedInfo.originalContainer, this.sharedInfo.originalIndex);
                this.sharedInfo.targetContainer = this.sharedInfo.originalContainer;
                this.sharedInfo.index = this.sharedInfo.originalIndex;
            }
        }

        return this.sharedInfo;
    }

    onDragDrop(event: any, dest: DNDContainerDirective): DNDInfo {
        this.sharedInfo.mode = DNDMode.DROP;

        this.sharedInfo.originalItem.decorateForMode(this.sharedInfo.mode);
        if (this.sharedInfo.targetItem) {
            this.sharedInfo.targetItem.decorateForMode(this.sharedInfo.mode);
            this.sharedInfo.targetItem = undefined;
        }

        return this.sharedInfo;
    }

    isDropAllowed(event: any, dest: DNDContainerDirective): boolean {

        if (!this.sharedInfo || !this.sharedInfo.originalItem) { // degenerate case at the very start of a drag
            return false;

        } else if (!dest.dropEnabled) { // drop is disabled by fiat.
            return false;

        } else if (dest.allowDrop) { // else if there's a function, use that.
            return dest.allowDrop(this.sharedInfo.dragData, this.sharedInfo.originalItem.declaredTypes);

        } else if (dest.allowedTypes.length === 0) {  // else if allowedTypes === [], that means anything
            return true;

        } else {
            // return true iff there is some intersection between the two lists.
            return dest.allowedTypes.some((type: string) => this.sharedInfo.originalItem.declaredTypes.indexOf(type) !== -1);
        }
    }

    private removeFromCurrentMidDrag(event: any): void {

        // if item doesn't have a current home, nothing to remove
        if (!this.sharedInfo.targetContainer) {
            return;

            // if we're dragging OUT OF original container, and the container is copyNotMove,
        } else if (this.sharedInfo.targetContainer === this.sharedInfo.originalContainer && this.sharedInfo.originalContainer.copyNotMove) {
            // don't delete the item from the existing container.
            return;

        } else if (this.sharedInfo.targetContainer.isSortable) {
            // delete item from existing container
            this.sharedInfo.targetContainer.sortableData.splice(this.sharedInfo.index, 1);
        }
    }

    private insertToTargetMidDrag(event: any, targetContainer: DNDContainerDirective, targetIndex: number): void {

        // if we're dragging INTO original container
        if (targetContainer === this.sharedInfo.originalContainer && this.sharedInfo.originalContainer.copyNotMove) {

            // moving back into original container ==> this is either a sort or an abort.
            this.sharedInfo.mode = (this.sharedInfo.originalContainer.isSortable ? DNDMode.SORT : DNDMode.CANCEL);

        } else if (targetContainer.isSortable) {

            // construct item with appropriate conversion for this container, if any
            let dragData: any = this.sharedInfo.dragData;
            if (targetContainer.dataConvert && this.sharedInfo.originalItem.container !== targetContainer) {
                dragData = targetContainer.dataConvert(dragData);
            }

            // insert
            targetContainer.sortableData.splice(targetIndex, 0, dragData);

            // this is a move or copy
            this.sharedInfo.mode = this.sharedInfo.moveOrCopy;
        }

    }

    private moveInCurrentMidDrag(event: any, alt: DNDItemDirective): void {
        // assert(this.sharedInfo.targetContainer == alt.container);

        if (alt.container.isSortable) {
            // no data conversion necessary
            let item: any = this.sharedInfo.targetContainer.sortableData[this.sharedInfo.index];
            // remove item from previous list.
            this.sharedInfo.targetContainer.sortableData.splice(this.sharedInfo.index, 1);
            // insert into current list
            alt.container.sortableData.splice(alt.index, 0, item);
        }
    }

}
